/**
 * @file 読書の記録が StudyProgressLog.detail に載せる JSON の型と parse/serialize。
 *
 * @what: 読んだ本 (カタログの 1 冊、またはカタログ外の本) と「また読みたい」を持つ。
 * @why: 読書のために新しい器を作らず、既存の StudyProgressLog を realm = reading で流用する。
 *   detail の語彙はサーバでなく web が持つ (サーバは有効な JSON かどうかしか見ない)。
 *   書名を必ず detail に残すのは、カタログが次の年度版に差し替わっても記録が読めなくなっては
 *   いけないためである。
 * @failure: parse は例外を投げない。壊れた detail・知らない形は null を返し、一覧では
 *   その記録を落とす (既読の印が付かないだけで、記録そのものは消えない)。
 */

/** 読書の記録の種別。detail の先頭に置いて、他の機能の detail と取り違えないようにする。 */
export const READING_LOG_KIND = "reading";

export interface ReadingLogBook {
  /** カタログの id。カタログ外の本では持たない。 */
  catalog_id?: string;
  /** 出典の段階の記号。カタログ外の本では持たない。 */
  level?: string;
  /** その段階の中の通し番号。カタログ外の本では持たない。 */
  no?: number;
  /** 記録した時点の書名 (必須)。 */
  title: string;
  /** 分かれば入れる。表紙を引く鍵になる。 */
  isbn?: string;
}

export interface ReadingLogDetail {
  kind: typeof READING_LOG_KIND;
  book: ReadingLogBook;
  /** また読みたがったか。省略可。子の評価ではなく本の記録として扱う。 */
  again?: boolean;
}

/** カタログの 1 冊を指す鍵 (既読の印の照合に使う)。カタログ外の本では null。 */
export function bookKey(book: ReadingLogBook | undefined): string | null {
  if (!book?.catalog_id || !book.level || book.no === undefined) return null;
  return `${book.catalog_id}/${book.level}/${book.no}`;
}

/** カタログの 1 冊から、上と同じ形の鍵を作る。 */
export function catalogBookKey(catalogId: string, level: string, no: number): string {
  return `${catalogId}/${level}/${no}`;
}

/**
 * 読書の記録かを判別する。
 *
 * @what: detail の kind が第 1 手掛かり。文字列の前置き判定で絞ってから JSON parse で確定し、
 *   書名などに同じ文字列が紛れても誤判定しない (きき分けあそびと同じやり方)。
 */
export function parseReadingLog(log: { detail?: string | undefined }): ReadingLogDetail | null {
  const d = log.detail;
  if (d === undefined || !d.includes(READING_LOG_KIND)) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(d);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const o = parsed as { kind?: unknown; book?: unknown; again?: unknown };
  if (o.kind !== READING_LOG_KIND) return null;
  if (typeof o.book !== "object" || o.book === null) return null;
  const raw = o.book as Record<string, unknown>;
  if (typeof raw.title !== "string" || raw.title === "") return null;

  const book: ReadingLogBook = { title: raw.title };
  if (typeof raw.catalog_id === "string") book.catalog_id = raw.catalog_id;
  if (typeof raw.level === "string") book.level = raw.level;
  if (typeof raw.no === "number" && Number.isInteger(raw.no)) book.no = raw.no;
  if (typeof raw.isbn === "string") book.isbn = raw.isbn;

  const detail: ReadingLogDetail = { kind: READING_LOG_KIND, book };
  if (o.again === true) detail.again = true;
  return detail;
}

/** 記録が読書のものかだけを見る (一覧からの除外に使う)。 */
export function looksLikeReadingLog(log: {
  detail?: string | undefined;
  realm?: string | undefined;
}): boolean {
  return log.realm === "reading" || parseReadingLog(log) !== null;
}

/** detail に載せる文字列を作る。 */
export function serializeReadingLog(book: ReadingLogBook, again: boolean): string {
  const detail: ReadingLogDetail = { kind: READING_LOG_KIND, book };
  if (again) detail.again = true;
  return JSON.stringify(detail);
}
